import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Pill, ReconBanner, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function Reconciliation() {
  const { accounts } = useApp();
  const nav = useNavigate();
  const [stmts, setStmts] = useState([]);
  useEffect(() => { api.get("/statements").then((r) => setStmts(r.data.filter((s) => ["verified", "failed"].includes(s.status)))); }, []);
  const failed = stmts.filter((s) => s.status === "failed");
  return (
    <div>
      <PageHeader title="Bank Reconciliation" sub="A matching closing balance alone is not proof. Each statement is checked for balance equation, running-balance continuity, page completeness, unparsed rows, confidence, period and duplicates." />
      {stmts.length > 0 && <ReconBanner verified={failed.length === 0} detail={failed.length ? `${failed.length} of ${stmts.length} statements have failing mandatory checks` : `${stmts.length} statements verified`} />}
      {stmts.length === 0 && <Empty title="No processed statements" sub="Upload a statement to see reconciliation checks." />}
      <div className="mt-6 space-y-8">
        {accounts.map((a) => {
          const list = stmts.filter((s) => s.account_id === a.id).sort((x, y) => (x.period_start || "").localeCompare(y.period_start || ""));
          if (!list.length) return null;
          return (
            <div key={a.id}>
              <h3 className="mb-3 font-semibold">{a.bank_name} <span className="num text-sm text-muted-foreground">{a.masked}</span> <span className="ml-2 text-xs text-muted-foreground">Tally ledger: {a.tally_ledger}</span></h3>
              <div className="panel overflow-x-auto">
                <table className="w-full min-w-[900px] text-sm">
                  <thead className="text-left text-xs text-muted-foreground"><tr>{["Period", "Statement", "Opening", "Credits", "Debits", "Closing", "Rows", "Failing checks", "Status", ""].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
                  <tbody>{list.map((s, i) => {
                    const prev = list[i - 1];
                    const gap = prev && prev.closing_balance !== s.opening_balance;
                    return (
                      <tr key={s.id} className="border-t" data-testid="recon-statement-row">
                        <td className="num p-3 text-xs">{fmtDate(s.period_start)} – {fmtDate(s.period_end)}</td>
                        <td className="max-w-[200px] truncate p-3 text-xs">{s.filename}</td>
                        <td className={`num p-3 ${gap ? "text-rose-500" : ""}`} title={gap ? `Previous closing ${prev.closing_balance}` : ""}>{inr(s.opening_balance)}</td>
                        <td className="num p-3 text-emerald-500">{inr(s.total_credits)}</td><td className="num p-3 text-rose-500">{inr(s.total_debits)}</td>
                        <td className="num p-3">{inr(s.closing_balance)}</td><td className="num p-3">{s.row_count}</td>
                        <td className="p-3 text-xs">{(s.checks || []).filter((c) => c.status === "fail").map((c) => c.label).join(", ") || "—"}</td>
                        <td className="p-3"><Pill s={s.status} testid="recon-status" /></td>
                        <td className="p-3"><Button size="sm" variant="outline" onClick={() => nav(`/statements/${s.id}`)} data-testid="recon-open-button">Review</Button></td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
              {list.some((s, i) => i > 0 && list[i - 1].closing_balance !== s.opening_balance) && <div className="mt-2 text-xs text-rose-500">Opening balance does not match previous statement's closing — a statement period may be missing.</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
